import { useCallback, useEffect, useState } from "react";
import { api } from "../../api/client";
import { useTabParam } from "../../hooks/useTabParam";

interface Claim { id: number; employee_id: number; employee_name?: string; title: string; status: string; total_amount: number; currency: string; submitted_at: string | null; approved_at: string | null; paid_at: string | null; }
interface Category { id: number; name: string; requires_receipt: boolean; max_amount: number | null; }

const EXPENSES_TABS = ["pending", "all", "categories"] as const;

export default function AdminExpensesPage() {
  const [pending, setPending] = useState<Claim[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [tab, setTab] = useTabParam(EXPENSES_TABS, "pending");
  const [showCatForm, setShowCatForm] = useState(false);

  const load = useCallback(async () => {
    try {
      const [p, c] = await Promise.all([
        api.get<Claim[]>("/api/expenses/pending"),
        api.get<Category[]>("/api/expenses/categories"),
      ]);
      setPending(p); setCategories(c);
    } catch {}
  }, []);
  useEffect(() => { void load(); }, [load]);

  const approve = async (id: number) => { await api.put(`/api/expenses/${id}/approve`); void load(); };
  const reject = async (id: number) => { const reason = prompt("Reason for rejection:"); if (!reason) return; await api.put(`/api/expenses/${id}/reject`, { reason }); void load(); };
  const markPaid = async (id: number) => { await api.put(`/api/expenses/${id}/mark-paid`); void load(); };

  const statusTone: Record<string, string> = {
    draft: "bg-gray-100 text-gray-600", submitted: "bg-amber-50 text-amber-800",
    approved: "bg-green-50 text-green-700", rejected: "bg-red-50 text-red-700",
    paid: "bg-blue-50 text-blue-700", cancelled: "bg-gray-100 text-gray-500",
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold text-gray-900">Expenses</h1>
        {tab === "categories" && (
          <button type="button" onClick={() => setShowCatForm(true)} className="rounded-lg bg-pme-blue px-4 py-2 text-sm font-semibold text-white">+ Add Category</button>
        )}
      </div>
      <nav className="flex gap-1 border-b border-gray-200">
        <TabBtn active={tab === "pending"} onClick={() => setTab("pending")}>Pending ({pending.filter((c) => c.status === "submitted").length})</TabBtn>
        <TabBtn active={tab === "all"} onClick={() => setTab("all")}>All Claims</TabBtn>
        <TabBtn active={tab === "categories"} onClick={() => setTab("categories")}>Categories</TabBtn>
      </nav>

      {showCatForm && (
        <CategoryForm onSaved={() => { setShowCatForm(false); void load(); }} onCancel={() => setShowCatForm(false)} />
      )}

      {(tab === "pending" || tab === "all") && (
        <div className="space-y-2">
          {pending.map((c) => (
            <div key={c.id} className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <p className="text-sm font-semibold text-gray-900">{c.employee_name ?? `Employee #${c.employee_id}`} — {c.title}</p>
                  <p className="text-xs text-gray-500">{c.currency} {c.total_amount.toFixed(2)}{c.submitted_at ? ` · Submitted ${new Date(c.submitted_at).toLocaleDateString()}` : ""}</p>
                </div>
                <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${statusTone[c.status] ?? "bg-gray-100"}`}>{c.status}</span>
              </div>
              <div className="mt-2 flex flex-wrap gap-1">
                {c.status === "submitted" && (
                  <>
                    <button type="button" onClick={() => void approve(c.id)} className="min-h-[36px] rounded bg-green-600 px-3 py-1 text-xs font-semibold text-white">Approve</button>
                    <button type="button" onClick={() => void reject(c.id)} className="min-h-[36px] rounded bg-red-600 px-3 py-1 text-xs font-semibold text-white">Reject</button>
                  </>
                )}
                {c.status === "approved" && (
                  <button type="button" onClick={() => void markPaid(c.id)} className="min-h-[36px] rounded bg-pme-blue px-3 py-1 text-xs font-semibold text-white">Mark Paid</button>
                )}
              </div>
            </div>
          ))}
          {pending.length === 0 && <p className="text-sm text-gray-400">No claims.</p>}
        </div>
      )}

      {tab === "categories" && (
        <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 text-left text-xs uppercase text-gray-500">
              <tr>
                <th className="px-3 py-2">Category</th>
                <th className="px-3 py-2">Receipt Required</th>
                <th className="px-3 py-2">Max Amount</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {categories.map((c) => (
                <tr key={c.id}>
                  <td className="px-3 py-2 font-medium text-gray-900">{c.name}</td>
                  <td className="px-3 py-2">{c.requires_receipt ? "Yes" : "No"}</td>
                  <td className="px-3 py-2">{c.max_amount ? `£${c.max_amount.toFixed(2)}` : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function CategoryForm({ onSaved, onCancel }: { onSaved: () => void; onCancel: () => void }) {
  const [name, setName] = useState("");
  const [requiresReceipt, setRequiresReceipt] = useState(true);
  const [maxAmount, setMaxAmount] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault(); setSubmitting(true);
    try {
      await api.post("/api/expenses/categories", { name, requires_receipt: requiresReceipt, max_amount: maxAmount ? Number(maxAmount) : null });
      onSaved();
    } catch {} finally { setSubmitting(false); }
  };

  return (
    <form onSubmit={submit} className="rounded-xl border border-gray-200 bg-white p-5 space-y-3">
      <h3 className="text-sm font-semibold text-gray-900">New Category</h3>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <label className="block text-sm"><span className="text-xs font-medium text-gray-700">Name</span>
          <input value={name} onChange={(e) => setName(e.target.value)} required placeholder="Travel" className="input mt-1" />
        </label>
        <label className="block text-sm"><span className="text-xs font-medium text-gray-700">Max Amount (optional)</span>
          <input type="number" step="0.01" value={maxAmount} onChange={(e) => setMaxAmount(e.target.value)} placeholder="500.00" className="input mt-1" />
        </label>
        <label className="flex items-center gap-2 text-sm pt-5">
          <input type="checkbox" checked={requiresReceipt} onChange={(e) => setRequiresReceipt(e.target.checked)} /> Requires receipt
        </label>
      </div>
      <div className="flex gap-2">
        <button type="submit" disabled={submitting} className="rounded-lg bg-pme-blue px-4 py-2 text-sm font-semibold text-white disabled:opacity-60">{submitting ? "Saving..." : "Save"}</button>
        <button type="button" onClick={onCancel} className="rounded-lg border border-gray-300 px-4 py-2 text-sm">Cancel</button>
      </div>
    </form>
  );
}

function TabBtn({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return <button type="button" onClick={onClick} className={["px-4 py-2 text-sm font-semibold", active ? "border-b-2 border-pme-blue text-pme-blue" : "text-gray-500"].join(" ")}>{children}</button>;
}
